<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="loader === true"
      variant="dark"
      opacity="0.85"
      blur="2px"
      rounded="sm"
      spinner-variant="primary"
      spinner-medium
    >
      <b-alert
        variant="danger"
        :show="alertErrors.length > 0"
        class="mb-0"
      >
        <div
          v-for="(row, index) in alertErrors"
          :key="index"
          class="alert-body"
        >
          <feather-icon

            icon="InfoIcon"
            class="mr-50"
          />
          {{ row }}
        </div>
      </b-alert>
      <b-card
        aria-hidden="true"
        class="mb-0"
      >
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >

          <!-- User Info: Input Fields -->
          <b-form
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <b-row>

              <!-- Field: Full Name -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_NAME')"
                    label-for="full-name"
                  >
                    <b-form-input
                      id="full-name"
                      v-model="useUserData.name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Email -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Email"
                  rules="required|email"
                >
                  <b-form-group
                    :label="$t('DEBTIA_EMAIL')"
                    label-for="email"
                  >
                    <b-form-input
                      id="email"
                      v-model="useUserData.email"
                      type="email"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Phone -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Phone"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PHONE')"
                    label-for="phone"
                  >
                    <b-form-input
                      id="phone"
                      v-model="useUserData.phone"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Contact Person -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CONTACT_PERSON')"
                  label-for="contact-person"
                >
                  <b-form-input
                    id="contact-person"
                    v-model="useUserData.contact_person"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Client Type -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CLIENT_TYPE')"
                  label-for="client-type"
                >
                  <v-select
                    v-model="useUserData.debtor_type"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="typeOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="client-type"
                    :value="'private-person'"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: CVRCPR -->
              <b-col
                v-if="useUserData.debtor_type === 'capital-company' || useUserData.debtor_type === 'sole-proprietorship'"
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="CVR"
                  rules="integer|length:8"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CVRCPR')"
                    label-for="cvrcpr"
                  >
                    <b-form-input
                      id="cvrcpr"
                      v-model="useUserData.cvr_number"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

              </b-col>

              <!-- Field: CPR -->
              <b-col
                v-if="useUserData.debtor_type === 'person'"
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CPR')"
                  label-for="cpr"
                >
                  <cleave
                    id="cpr"
                    v-model="useUserData.cpr"
                    class="form-control"
                    :raw="false"
                    :options="options.cprDelimiter"
                  />
                </b-form-group>

              </b-col>

            </b-row>

            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_ADDRESS') }}</h4></b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="House Number"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_HOUSE_NUMBER')"
                    label-for="house-number"
                  >
                    <b-form-input
                      id="house-number"
                      v-model="useUserData.address.house_number"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Road Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ROAD_NAME')"
                    label-for="road-name"
                  >
                    <b-form-input
                      id="road-name"
                      v-model="useUserData.address.road_name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Place -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_PLACE_NAME')"
                  label-for="place"
                >
                  <b-form-input
                    id="place"
                    v-model="useUserData.address.place"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Zipcode -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Zip Code"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ZIP_CODE')"
                    label-for="zip-code"
                  >
                    <b-form-input
                      id="zip-code"
                      v-model="useUserData.address.zip_code"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getCityName($event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: City -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="City"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CITY')"
                    label-for="city"
                  >
                    <b-form-input
                      id="city"
                      v-model="useUserData.address.city"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Country -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Country"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_COUNTRY')"
                    label-for="country"
                  >
                    <v-select
                      v-model="useUserData.address.country_id"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="$t('COUNTRIES_DROP_DOWN')"
                      :reduce="val => val.value"
                      :clearable="false"
                      input-id="select-country"
                      :value="'Select Country'"
                    >
                      <template
                        #selected-option-container="{ option }"
                      >
                        <div class="vs__selected">
                          {{ $t(option.label) }}</div>
                      </template>
                      <template
                        slot="option"
                        slot-scope="option"
                      >
                        {{ $t(option.label) }}
                      </template>
                    </v-select>
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

            </b-row>
            <hr>

            <!-- Action Buttons -->
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              type="submit"
            >
              Save Changes
            </b-button>
            <b-button
              variant="outline-secondary"
              type="reset"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            >
              Reset
            </b-button>
          </b-form>

        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton, BRow, BCol, BFormGroup, BFormInput, BForm, BCard,
  BFormInvalidFeedback, BOverlay, BAlert,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { avatarText } from '@core/utils/filter'
import vSelect from 'vue-select'
import { ref/* , onUnmounted  */ } from '@vue/composition-api'
import axios from '@axios'
import store from '@/store'
import constants from '@/app-configs/constants'
import Cleave from 'vue-cleave-component'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'cleave.js/dist/addons/cleave-phone.us'
import countries from '@/countries'

import formValidation from '@core/comp-functions/forms/form-validation'
import { required, alphaNum, email } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import clientStoreModule from '../debtorStoreModule'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BCard,
    vSelect,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
    BFormInvalidFeedback,
    BOverlay,
    BAlert,
    Cleave,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      countries,
      required,
      alphaNum,
      email,
      alertErrors: [],
      useUserData: { ...this.userData },
      // validateUsername: true,
      options: {
        cprDelimiter: {
          delimiters: ['-'],
          blocks: [6, 4],
          uppercase: true,
        },
      },
    }
  },
  setup(props) {
    const loginOptions = [
      { label: 'Allowed', value: '1' },
      { label: 'Not Allowed', value: '0' },
    ]
    const loader = false

    const refInputEl = ref(null)
    const previewEl = ref(null)

    const {
      refFormObserver,
      getValidationState,
    } = formValidation(props.userData)

    const typeOptions = [
      { label: 'Person', value: 'person' },
      { label: 'Sole Proprietorship', value: 'sole-proprietorship' },
      { label: 'Capital Company', value: 'capital-company' },
    ]

    return {
      avatarText,
      loginOptions,
      loader,
      refInputEl,
      previewEl,
      refFormObserver,
      getValidationState,
      BFormInvalidFeedback,
      typeOptions,
    }
  },
  methods: {
    getCityName(event) {
      axios.get(`${constants.debtiaappUrl}/settings/get-city-name-by-code?code=${event}`)
        .then(response => {
          this.userData.address.city = response.data.data.city_name
        }).catch(error => {
          console.log(error)
        })
    },
    async onSubmit() {
      this.useUserData.type = this.useUserData.debtor_type
      this.useUserData.cvrcpr = this.useUserData.cvr_number
      // this.useUserData.birth_date = this.useUserData.birthdate

      const data = { ...this.useUserData }
      // const validation = await axios.post('debtor/validate-username', { username: data.username, exclude: [data.id] })
      //   .then(() => true).catch(() => false)

      // if (!validation) {
      //   this.validateUsername = validation
      //   return false
      // }
      this.loader = true
      const USER_APP_STORE_MODULE_NAME = 'app-debtor'

      // Register module
      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, clientStoreModule)
      // console.log('--->', data)
      store.dispatch('app-debtor/editDebtor', data)
        .then(() => {
          // console.log('done', response)
          this.$router.replace('/debtors/list')
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Debtor Updated',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: 'Debtor successfully Updated!',
                },
              })
            })
        })
        .catch(error => {
          this.alertErrors = []
          this.alertErrors.push(...error.data.message)
          // console.log('error**', this.alertErrors)
          this.loader = false
        })
      return true
    },

    resetForm() {
      this.useUserData = { ...this.userData }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.spinner-border{
  width: 6rem;
  height: 6rem;
}
</style>
